import { DatePipe } from '@angular/common';
import { Component, Input, ViewChild } from '@angular/core';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslatePipe } from '@ngx-translate/core';
import { MoodleService, CourseStructure } from '../../../services/moodle.service';
import { BucketService, ICourse } from '../../../services/bucket.service';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { MoodleImportDialogComponent } from '../../dialogs/moodle-import-dialog/moodle-import-dialog.component';
import { MoodlePushDialogComponent } from '../../dialogs/moodle-push-dialog/moodle-push-dialog.component';
import { CourseStructureDialogComponent } from '../../dialogs/course-structure-dialog/course-structure-dialog.component';
import { CourseEditDialogComponent } from '../../dialogs/course-edit-dialog/course-edit-dialog.component';
import {
  AlTableApi,
  AlTableColumn,
} from '../../features/table/table.component';
import { StatusIconComponent } from '../../features/status-icon/status-icon.component';

@Component({
  selector: 'app-courses-table',
  templateUrl: './courses-table.component.html',
  styleUrl: './courses-table.component.scss',
})
export class CoursesTableComponent {
  @Input() title = '';
  @Input() bucketId = '';
  @Input() loading = false;
  @Input() searchText = '';

  get filteredData() {
    const data = this.bucketService._courses.value;
    if (!this.searchText) return data;
    const q = this.searchText.toLowerCase();
    return data.filter((c: any) => c.course_name?.toLowerCase().includes(q));
  }

  @ViewChild('table') table!: AlTableApi;

  courses: any[] = [];

  private syncFreqLabels: Record<string, string> = {};

  columns: AlTableColumn[] = [];

  private initColumns() {
    this.syncFreqLabels = {
      NEVER: this.t.transform('dialogs.courseEdit.freqNever'),
      DAILY: this.t.transform('dialogs.courseEdit.freqDaily'),
      MONTHLY: this.t.transform('dialogs.courseEdit.freqMonthly'),
      YEARLY: this.t.transform('dialogs.courseEdit.freqYearly'),
    };

    this.columns = [
      { key: 'course_name', title: this.t.transform('tables.courses.name'), width: '35%' },
      { key: 'source_type', title: this.t.transform('tables.courses.source') },
      { key: 'sections_count', kind: 'number', title: this.t.transform('tables.courses.sections') },
      { key: 'items_count', kind: 'number', title: this.t.transform('tables.courses.items') },
      {
        key: 'agent_enabled',
        title: 'Agent',
        map: (v: any) => (v === false ? 'Deaktiviert' : 'Durchsuchbar'),
        width: '110px',
      },
      {
        key: 'sync_frequency',
        title: this.t.transform('tables.courses.syncFrequency'),
        map: (val: string) => this.syncFreqLabels[val] || val || '-',
      },
      {
        key: 'created_at', kind: 'date',
        title: this.t.transform('tables.courses.createdAt'),
        map: (i: string) => new DatePipe('de-DE', 'CET').transform(i, 'short'),
      },
    ];
  }

  rowActions: any[] = [
    {
      key: 'toggleAgent',
      title: 'Für Agenten deaktivieren / aktivieren',
      icon: 'slash-outline',
    },
    {
      key: 'view',
      title: this.t.transform('buttons.view'),
      icon: 'eye-outline',
    },
    {
      key: 'edit',
      title: this.t.transform('buttons.edit'),
      icon: 'settings-outline',
    },
    {
      key: 'push',
      title: this.t.transform('tables.courses.pushAction'),
      icon: 'upload-outline',
      disabled: (row: ICourse) => !this.isCreatorCourse(row),
      tooltip: (row: ICourse) => this.getPushToMoodleTooltip(row),
    },
    {
      key: 'delete',
      title: this.t.transform('buttons.delete'),
      isMultiAction: true,
      icon: 'trash-outline',
    },
  ];

  globalActions = [
    {
      key: 'add',
      title: 'Moodle Import',
      icon: 'plus-outline',
    },
  ];

  constructor(
    protected moodleService: MoodleService,
    protected bucketService: BucketService,
    protected dialogService: NbDialogService,
    protected nbToastrService: NbToastrService,
    protected t: TranslatePipe
  ) {
    this.initColumns();
  }

  onEvent(e: { event: string; data: any }) {
    const { action, ids, rows, id, row } = e.data;

    switch (e.event) {
      case 'multiAction':
        if (action.key === 'delete') {
          this.deleteIds(ids, rows);
          return;
        }
        break;
      case 'rowAction':
        if (action.key === 'toggleAgent') {
          this.toggleCourseAgentAccess(row);
        }
        if (action.key === 'view') {
          this.viewCourseStructure(row);
          return;
        }
        if (action.key === 'edit') {
          this.editCourse(row);
          return;
        }
        if (action.key === 'push') {
          if (!this.isCreatorCourse(row)) {
            return;
          }
          this.pushToMoodle(row);
          return;
        }
        if (action.key === 'delete') {
          this.deleteIds([id], [row]);
          return;
        }
        break;
      case 'globalAction':
        if (action.key === 'add') {
          this.openMoodleImport();
          return;
        }
        break;
      default:
        break;
    }
  }

  openMoodleImport() {
    const dialogRef = this.dialogService.open(MoodleImportDialogComponent, {
      context: {
        bucketId: this.bucketId,
      },
    });

    dialogRef.onClose.subscribe(() => {
      this.bucketService.getBucketEntries(this.bucketId).subscribe();
    });
  }

  viewCourseStructure(course: any) {
    this.dialogService.open(CourseStructureDialogComponent, {
      context: {
        courseId: course.id || course.course_id,
        courseName: course.course_name,
      },
    });
  }

  editCourse(course: any) {
    const dialogRef = this.dialogService.open(CourseEditDialogComponent, {
      context: {
        courseId: course.id || course.course_id,
        courseName: course.course_name,
      },
    });

    dialogRef.onClose.subscribe((updated) => {
      if (updated) {
        this.bucketService.getBucketEntries(this.bucketId).subscribe();
      }
    });
  }

  pushToMoodle(course: any) {
    if (!this.isCreatorCourse(course)) {
      return;
    }

    const dialogRef = this.dialogService.open(MoodlePushDialogComponent, {
      context: {
        courseId: course.id || course.course_id,
        courseName: course.course_name,
      },
    });

    dialogRef.onClose.subscribe((pushed) => {
      if (pushed) {
        this.bucketService.getBucketEntries(this.bucketId).subscribe();
      }
    });
  }

  isCreatorCourse(course: Pick<ICourse, 'source_type'> | null | undefined): boolean {
    return (course?.source_type || '').toLowerCase() === 'alphatutor';
  }

  getPushToMoodleTooltip(course: Pick<ICourse, 'source_type'> | null | undefined): string {
    return this.t.transform(
      this.isCreatorCourse(course)
        ? 'tables.courses.pushActionTooltip'
        : 'tables.courses.pushActionDisabledTooltip'
    );
  }

  /** Ganzen Kurs vom Agenten ausnehmen — oder wieder freigeben.
   *
   *  Ein Kurs ist keine einzelne Datatype, sondern bündelt viele Inhalts-
   *  elemente mit je eigener. Der Schalter setzt deshalb alle auf einmal;
   *  der angezeigte Zustand wird umgekehrt daraus abgeleitet (aus, sobald
   *  jedes verknüpfte Element aus ist).
   */
  toggleCourseAgentAccess(row: any) {
    const courseId = row?.id;
    if (!courseId) return;
    const next = row.agent_enabled === false;
    this.bucketService.setCourseAgentAccess(courseId, next).subscribe({
      next: (res: any) => {
        row.agent_enabled = next;
        row.agent_disabled_items = next ? 0 : res?.items_total ?? 0;
        this.nbToastrService.success(
          next
            ? 'Die Kursinhalte werden wieder durchsucht.'
            : `Kursinhalte vom Agenten ausgenommen (${res?.items_updated ?? 0} Elemente).`,
          'Wissensbasis'
        );
      },
      error: () =>
        this.nbToastrService.danger('Änderung fehlgeschlagen', 'Wissensbasis'),
    });
  }

  deleteIds(ids: string[], rows: any[]) {
    this.dialogService
      .open(ConfirmDialogComponent, {
        context: {
          title: 'dialogs.confirmDialog.deleteEntries.title',
          message: 'dialogs.confirmDialog.deleteEntries.message',
        },
      })
      .onClose.subscribe((confirmed) => {
        if (confirmed) {
          this.table.isLoading = true;
          let completed = 0;
          const courseIds = rows.map((r) => r.id || r.course_id);

          courseIds.forEach((courseId) => {
            this.moodleService.deleteCourse(courseId).subscribe({
              next: () => {
                completed++;
                if (completed === courseIds.length) {
                  this.table.deselectAll();
                  this.table.isLoading = false;
                  this.bucketService
                    .getBucketEntries(this.bucketId)
                    .subscribe();
                  this.nbToastrService.success(
                    'Kurse wurden gelöscht.',
                    'Erfolgreich'
                  );
                }
              },
              error: () => {
                completed++;
                if (completed === courseIds.length) {
                  this.table.isLoading = false;
                }
              },
            });
          });
        }
      });
  }
}
